
import type { ResultInnerDetail } from '@empathybroker/tsclient-play/dist/models/search/result'
import type {
  QueryRankingElementRankingDTO,
  QueryRankingHiddenElementDTO,
  QueryRankingPinnedElementDTO,
} from '@empathyco/playboard-core-domain'
import type { Result } from '@empathyco/x-types'
import type { PropType, SetupContext } from 'vue'
import type { QueryRankingElementActionConfig } from '../../composables/use-query-ranking-element-actions-config'
import type { ToolsMessages } from '../../models'
import type { QueryRankingDomainService } from '../../services/interfaces/domain/query-ranking-domain.service'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import {
  HiddenElementsAction,
  PinProductAction,
  QueryRankingElementRankingAction,
} from '@empathyco/playboard-core-domain'
import { computed, defineComponent } from 'vue'
import ResultCard from '../../components/result-card.component.vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { ConfigurationsUtils, ExplainUtils, ScheduleUtils } from '../../utils'
import ResultCardFooter from '../result-card-footer.component.vue'
import QueryRankingActionButtons from './query-ranking-action-buttons.component.vue'

export default defineComponent({
  name: 'ResultCardQueryRanking',
  components: {
    ResultCardFooter,
    QueryRankingActionButtons,
    ResultCard,
  },
  props: {
    result: {
      type: Object as PropType<Result>,
      required: true,
    },
    date: {
      type: Date,
      required: true,
    },
    productsRanking: {
      type: Array as PropType<Array<QueryRankingElementRankingDTO>>,
      required: true,
    },
    attributesRanking: {
      type: Array as PropType<Array<QueryRankingElementRankingDTO>>,
      required: true,
    },
    hiddenAttributes: {
      type: Array as PropType<Array<QueryRankingHiddenElementDTO>>,
      required: true,
    },
    pinnedProducts: {
      type: Array as PropType<Array<QueryRankingPinnedElementDTO>>,
      required: true,
    },
    hiddenProducts: {
      type: Array as PropType<Array<QueryRankingHiddenElementDTO>>,
      required: true,
    },
    messages: {
      type: Object as PropType<ToolsMessages['configurations']>,
      required: true,
    },
    endpointId: {
      type: String,
      default: DomainEnum.SEARCH,
    },
    isReadOnly: Boolean,
  },
  emits: [
    'boostingProduct',
    'removeBoostedProduct',
    'pinProduct',
    'unPinProduct',
    'hideProduct',
    'unHideProduct',
  ],
  setup(props, ctx: SetupContext) {
    const resultId = props.result.id.toString()

    const productAction = computed<QueryRankingElementActionConfig['action'] | undefined>(() =>
      getProductAction(),
    )

    const qrDomainService = CONTAINER.get<QueryRankingDomainService>(
      TYPES.QueryRankingDomainService,
    )

    const actionsButtons = computed(() => {
      const actions: Array<QueryRankingElementActionConfig['action']> = [
        QueryRankingElementRankingAction.BOOST,
        QueryRankingElementRankingAction.BURY,
      ]

      actions.push(PinProductAction)
      actions.push(HiddenElementsAction)

      if (productAction.value) {
        return actions.filter(action => productAction.value === action)
      }

      return actions
    })

    function getProductAction(): QueryRankingElementActionConfig['action'] | undefined {
      if (
        ConfigurationsUtils.findQueryRankingConfigByFieldValue(resultId, props.pinnedProducts) ||
        ConfigurationsUtils.findQueryRankingConfigByFieldValue(resultId, props.productsRanking) ||
        ConfigurationsUtils.findQueryRankingConfigByFieldValue(resultId, props.hiddenProducts)
      ) {
        return ExplainUtils.getQueryRankingConfigActionFromResult(props.result, isHidden.value)
      }
    }

    function onActionClick(event: {
      action: QueryRankingElementActionConfig['action']
      selected: boolean
    }): void {
      if (event.action === PinProductAction) {
        onPinProduct(event.action, event.selected)
      } else if (event.action === HiddenElementsAction) {
        onHideProduct(event.action, event.selected)
      } else {
        onBoostOrBuryProduct(event.action, event.selected)
      }
    }

    function onBoostOrBuryProduct(
      action: QueryRankingElementRankingDTO['action'],
      selected: boolean,
    ): void {
      if (selected) {
        ctx.emit('boostingProduct', [
          qrDomainService.createProductRanking({ action, fieldValue: resultId }),
        ])
      } else {
        ctx.emit('removeBoostedProduct', resultId)
      }
    }

    function onPinProduct(action: typeof PinProductAction, selected: boolean): void {
      if (selected) {
        ctx.emit(
          'pinProduct',
          qrDomainService.createPinnedProduct({
            fieldValue: resultId,
            action,
            position: undefined,
          }),
        )
      } else {
        ctx.emit('unPinProduct', resultId)
      }
    }

    function onHideProduct(action: typeof HiddenElementsAction, selected: boolean): void {
      if (selected) {
        ctx.emit('hideProduct', [
          qrDomainService.createHiddenProduct({ action, fieldValue: resultId }),
        ])
      } else {
        ctx.emit('unHideProduct', resultId)
      }
    }

    const queryRanking = computed(() =>
      ExplainUtils.getQueryRankingDetailsFromResultScoreCriteria(props.result),
    )

    function isInAttributesRanking(detail?: ResultInnerDetail): boolean {
      return props.attributesRanking.some(
        attributeRanking =>
          detail?.fieldValue === attributeRanking.fieldValue &&
          detail?.fieldName.includes(attributeRanking.fieldName) &&
          ExplainUtils.getQueryRankingElementConfigAction(detail.type) === attributeRanking.action,
      )
    }

    const isBoostedByAttribute = computed(() =>
      queryRanking.value?.attributes?.details.some(
        detail =>
          isInAttributesRanking(detail) &&
          ExplainUtils.getQueryRankingElementConfigAction(detail.type) ===
            QueryRankingElementRankingAction.BOOST,
      ),
    )

    const isBuriedByAttribute = computed(() =>
      queryRanking.value?.attributes?.details.some(
        detail =>
          isInAttributesRanking(detail) &&
          ExplainUtils.getQueryRankingElementConfigAction(detail.type) ===
            QueryRankingElementRankingAction.BURY,
      ),
    )

    const isHidden = computed(() => {
      const hiddenConfig = ConfigurationsUtils.findQueryRankingConfigByFieldValue(
        resultId,
        props.hiddenProducts,
      )
      return hiddenConfig && ScheduleUtils.isDateWithinSchedule(props.date, hiddenConfig.schedule)
    })

    return {
      baseClass: 'epb-result-card-query-ranking',
      productAction,
      actionsButtons,
      onBoostOrBuryProduct,
      onPinProduct,
      onActionClick,
      isBoostedByAttribute,
      isBuriedByAttribute,
      isHidden,
    }
  },
})
